import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { CheckIcon } from "lucide-react";
import { Label } from "@/core/components/ui/Label";
import { cn } from "@core/utils/domUtil";
import { ReactNode } from "react";

function Checkbox({ className, ...props }: React.ComponentProps<typeof CheckboxPrimitive.Root>) {
  return (
    <CheckboxPrimitive.Root data-slot="checkbox" className={cn("peer border-input dark:bg-input/30 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground dark:data-[state=checked]:bg-primary data-[state=checked]:border-primary focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-error/20 dark:aria-invalid:ring-error/40 aria-invalid:border-error size-4 shrink-0 rounded-[4px] border shadow-xs transition-shadow outline-none focus-visible:ring-[2px] disabled:cursor-not-allowed disabled:opacity-50", className)} {...props}>
      <CheckboxPrimitive.Indicator data-slot="checkbox-indicator" className="flex items-center justify-center text-current transition-none">
        <CheckIcon className="size-3.5" />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

export interface CheckboxGroupProps {
  /** CheckboxGroupProps: 체크박스 고유 id */
  id: string;
  /** CheckboxGroupProps: 체크박스 체크 여부 */
  checked?: boolean;
  /** CheckboxGroupProps: 체크박스 체크 여부 변경 이벤트 */
  onChange?: (checked: boolean) => void;
  /** CheckboxGroupProps: 체크박스 크기 */
  size?: "normal" | "small";
  /** CheckboxGroupProps: 체크박스 레이블 텍스트 */
  label?: ReactNode | string;
  /** CheckboxGroupProps: 체크박스 자식 컴포넌트 */
  children?: ReactNode;
  /** CheckboxGroupProps: 체크박스 컴포넌트에 적용할 추가 클래스 */
  className?: string;

  disabled?: boolean;
}

function CheckboxGroup({ id, label, checked, onChange, size = "normal", children, className, disabled }: CheckboxGroupProps) {
  const checkboxClass = cn("absolute left-0 top-[50%] -translate-y-1/2", {
    "w-[22px] h-[22px]": size === "normal",
    "w-[16px] h-[16px]": size === "small",
  });

  const labelClass = cn("inline-block", {
    "text-lg": size === "normal",
    "text-md": size === "small",
  });

  return (
    <div
      className={cn(
        "relative inline-block",
        {
          "pl-[30px]": size === "normal",
          "pl-[22px]": size === "small",
        },
        className,
      )}
    >
      <Checkbox id={id} checked={checked} onCheckedChange={(value) => onChange?.(!!value)} className={checkboxClass} disabled={disabled} />
      <Label htmlFor={id} className={cn("leading-[var(--input-height-sm)] lg:leading-[var(--input-height-lg)]", labelClass)}>
        {children ?? label}
      </Label>
    </div>
  );
}

export { Checkbox, CheckboxGroup };
